import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import { getUser } from '@/redux/user';
import { useAuthCookieHandler } from '@/hooks/useAuthCookieHandler/useAuthCookieHandler';
import { UserRoles } from '@/services/innovorder/user/user.type';
import { AccessFlags } from '@/services/innovorder/auth/auth.type';
import {
    useGetUserQuery,
    useGetUserRestaurantsQuery,
    useUpdateUserRestaurantsMutation,
} from '@/services/innovorder/user/user.endpoints';
import { useGetBrandRestaurantsQuery } from '@/services/innovorder/brand/brand.endpoints';

interface Restaurant {
    restaurantId: number;
    name: string;
}

interface UseUserRestaurantsControlVM {
    handleSave: () => void;
    restaurants: Restaurant[];
    selectedRestaurantIds: Set<number>;
    handleToggle: (restaurantId: number) => void;
    isButtonDisabled: boolean;
    isButtonLoading: boolean;
    isLoading: boolean;
    hasPermission: boolean;
}

const useUserRestaurantsControlVM = (): UseUserRestaurantsControlVM => {
    const { params } = useRouteMatch<{ userId: string; brandId: string }>();
    const userId = Number(params.userId);
    const brandId = Number(params.brandId);

    const currentUser = useSelector(getUser);
    const { token } = useAuthCookieHandler();

    const [selectedRestaurantIds, setSelectedRestaurantIds] = useState<Set<number>>(new Set());
    const [initialRestaurantIds, setInitialRestaurantIds] = useState<Set<number>>(new Set());

    // Check if current user has permission to manage BO users
    const hasPermission = useMemo(() => {
        if (currentUser?.role === UserRoles.admin) return true;
        if (currentUser?.role === UserRoles.brand && currentUser?.accessFlags?.[AccessFlags.CAN_MANAGE_BO_USERS]) {
            return true;
        }
        return false;
    }, [currentUser?.role, currentUser?.accessFlags]);

    // Get target user data (to verify brand)
    const { data: targetUser, isLoading: isLoadingUser } = useGetUserQuery({ userId }, { skip: !hasPermission });

    // Get all brand restaurants
    const { data: brandRestaurants, isLoading: isLoadingBrandRestaurants } = useGetBrandRestaurantsQuery(
        { brandId, token: token || '' },
        { skip: !hasPermission || !token },
    );

    // Get user's assigned restaurants
    const { data: userRestaurants, isLoading: isLoadingUserRestaurants } = useGetUserRestaurantsQuery(
        { userId },
        { skip: !hasPermission },
    );

    const [updateUserRestaurants, { isLoading: isUpdating }] = useUpdateUserRestaurantsMutation();

    // Initialize selected restaurants when user restaurants data is loaded
    useEffect(() => {
        if (userRestaurants) {
            const ids = new Set(userRestaurants.map((r) => r.restaurantId));
            setSelectedRestaurantIds(ids);
            setInitialRestaurantIds(ids);
        }
    }, [userRestaurants]);

    const handleToggle = useCallback((restaurantId: number) => {
        setSelectedRestaurantIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(restaurantId)) {
                newSet.delete(restaurantId);
            } else {
                newSet.add(restaurantId);
            }
            return newSet;
        });
    }, []);

    const handleSave = useCallback(() => {
        updateUserRestaurants({
            userId,
            restaurantIds: Array.from(selectedRestaurantIds),
        }).then(() => {
            setInitialRestaurantIds(new Set(selectedRestaurantIds));
        });
    }, [userId, selectedRestaurantIds, updateUserRestaurants]);

    // Check if there are changes
    const hasChanges = useMemo(() => {
        if (selectedRestaurantIds.size !== initialRestaurantIds.size) return true;
        for (const id of selectedRestaurantIds) {
            if (!initialRestaurantIds.has(id)) return true;
        }
        return false;
    }, [selectedRestaurantIds, initialRestaurantIds]);

    // Build list of restaurants with their selection status
    const restaurants = useMemo(() => {
        if (!brandRestaurants) return [];
        return brandRestaurants
            .map((r) => ({
                restaurantId: r.restaurantId,
                name: r.name,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [brandRestaurants]);

    const isLoading = isLoadingUser || isLoadingBrandRestaurants || isLoadingUserRestaurants;

    return {
        handleSave,
        restaurants,
        selectedRestaurantIds,
        handleToggle,
        isButtonDisabled: !hasChanges,
        isButtonLoading: isUpdating,
        isLoading,
        hasPermission,
    };
};

export default useUserRestaurantsControlVM;
